/**
 * 가이드북
 */
import { Fragment } from 'react';
import Tags from '@components/contents/common/Tags';
import { setDateFormat } from '@utils/index';
import { GAME_NAME, GUIDEBOOK_TAG_URL, SERVICE_ALIAS } from '@constants/index';
import { useOption } from '@hooks/useOption';
import { useSearchParams } from 'react-router-dom';
import type { IGuidebook, ISetGuidebook, l10nType } from '@type/index';

export const setGuidebookItem = (item: IGuidebook, serviceAlias: string, searchParams: URLSearchParams): ISetGuidebook | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const isAion = serviceAlias === SERVICE_ALIAS.AION;
  const isL2 = serviceAlias === SERVICE_ALIAS.LINEAGE2;
  const { guideName, updateTime, tags, gameName } = item;
  const prefixGameName = isAion
    ? gameName === GAME_NAME[serviceAlias].CLASSIC
      ? 'classic'
      : ''
    : isL2
      ? gameName === GAME_NAME[serviceAlias].L2AWKN
        ? GAME_NAME[serviceAlias].L2AWKN
        : GAME_NAME[serviceAlias].L2
      : '';
  const removeStrongGuideName = guideName?.replace(/\<strong>|\<\/strong>/g, ''); // <strong>텍스트</strong> 형태로 내려올 수 있음
  const url = guideName ? `/guidebook${prefixGameName ? `/${prefixGameName}` : ''}/view?title=${encodeURIComponent(removeStrongGuideName)}` : '';
  const convertDate = typeof updateTime === 'number' ? new Date(updateTime).toISOString() : updateTime;
  const tagBaseUrl =
    serviceAlias in GUIDEBOOK_TAG_URL
      ? (isAion || isL2) && prefixGameName
        ? GUIDEBOOK_TAG_URL[serviceAlias]?.replace('{GAME_NAME}', prefixGameName)
        : GUIDEBOOK_TAG_URL[serviceAlias]
      : `/guidebook/search#field=tags&keyword=`; // 가이드북 태그 클릭 시 이동할 url (hash는 Template.js에서 주입)
  const tagsArr = tags?.split(',')?.map((tag) => ({
    name: tag,
    url: `${tagBaseUrl}${tag}&page=1`,
  }));

  return {
    ...item,
    url,
    convertDate,
    tagsArr,
  };
};

const GuidebookArticle = ({ item, l10n }: { item: IGuidebook; l10n: l10nType }) => {
  const { serviceAlias } = useOption();
  const [searchParams] = useSearchParams();
  const data = setGuidebookItem(item, serviceAlias, searchParams);

  if (!data) {
    return <Fragment />;
  }

  const { url, thumbnailUrl, guideName, content, tagsArr, convertDate } = data;
  return (
    <Fragment>
      <div className="search-item-content">
        {thumbnailUrl ? (
          <div className="search-item-thumb">
            <a href={url} className="thumb-wrap" style={{ backgroundImage: `url(${thumbnailUrl})` }}></a>
          </div>
        ) : (
          <Fragment />
        )}
        <div className="search-item-link">
          <a href={url}>
            <div className="search-item-title" dangerouslySetInnerHTML={{ __html: guideName }}></div>
            {content ? <div className="search-item-summary" dangerouslySetInnerHTML={{ __html: content }}></div> : <Fragment />}
          </a>
        </div>
      </div>
      <Tags data={tagsArr} useSharpTag={true} />
      <div className="search-item-info">
        <span className="info-update">{l10n?.guidebook?.update}</span>
        <span className="date">{setDateFormat(convertDate, l10n)}</span>
      </div>
    </Fragment>
  );
};

export default GuidebookArticle;
